import React from 'react';
import { View, Text, TouchableOpacity } from 'react-native';
import i18n from 'i18n-js';

import ColorWrapper, { WithColor } from '@styles/colors';
import Svg from '@styles/icons/svg';
import { font as initialFont } from '@styles/fonts';
import { ContextImage } from './interfaces';
import { scale as initialScale } from '@components/frame';
import { SmallLoading } from '@components/loading';
import Image from '@components/common/Image';
import WebImageControls from './components/webImageControls';
import { GestureResponderEvent } from 'react-native-modal';
import { ScaleFunction } from '@components/operatorPanel/interfaces';
import CropperWeb from '@v2/components/imageHandling/components/Cropper/components/CropperWeb';
import { useManageContextImage } from './hooks/useManageContextImage';
import { ImageDocumentReference } from '@v2/components/imageHandling/types/types';

interface ContextProps {
  image: ContextImage;
  onPress?: (event: GestureResponderEvent) => void;
  showImageControl?: boolean;
  attachMode?: boolean;
  imageDocumentReferences?: ImageDocumentReference[];
}

export interface ContextInternalProps extends ContextProps, WithColor {
  noOverlay?: boolean;
  addImageIconColor?: string;
  scale?: ScaleFunction;
  font?: typeof initialFont;
  disableImageOverlay?: boolean;
}

const Context = ({
  image,
  imageDocumentReferences,
  C,
  onPress,
  showImageControl,
  attachMode,
  noOverlay,
  scale = initialScale,
  font = initialFont,
  addImageIconColor = 'Gray/Light/1',
  disableImageOverlay = false,
}: ContextInternalProps) => {
  const {
    showCrop,
    imageLink,
    originUrlImageLink,
    localRotation,
    saveCroppedImage,
    triggerCrop,
    showControls,
    handleImageNavigation,
    imageHandlingContext,
    pressRotateImage,
    pressImage,
    renderWidth,
    renderHeight,
    borderRadius,
    getTransformStyle,
    loading,
    possibleAddImage,
    uploadedImage,
  } = useManageContextImage({ image, attachMode, onPress, showImageControl, imageDocumentReferences });

  if (showCrop && imageLink && originUrlImageLink && image.imageData) {
    return (
      <CropperWeb
        image={{
          ...image.imageData,
          originUrl: originUrlImageLink,
          url: imageLink,
          base64: imageLink,
          fileName: image.imageData?.fileName || 'image',
          documentId: image.documentId,
          customRotate: { [image.imageData?.pageNumber ?? 1]: localRotation },
        }}
        shouldLoadDocument
        onSave={saveCroppedImage}
        onBack={triggerCrop}
        C={C}
      />
    );
  }

  return (
    <View
      style={[
        {
          width: image.width,
          height: image.height,
          justifyContent: 'center',
          alignItems: 'center',
        },
      ]}
    >
      {showControls && (
        <WebImageControls
          {...{
            openImage: () => handleImageNavigation(imageHandlingContext),
            rotateImage: pressRotateImage,
            cropImage: triggerCrop,
            pressImage,
            documentId: image.documentId,
            scale,
          }}
        />
      )}
      <View
        style={[
          {
            width: renderWidth,
            height: renderHeight,
            backgroundColor: noOverlay ? 'transparent' : 'Background / Light / System',
            justifyContent: 'center',
            alignItems: 'center',
            borderRadius: scale(borderRadius),
            ...getTransformStyle(),
          },
        ]}
      >
        {image.isProcessing && (
          <View
            style={{
              width: '100%',
              height: '100%',
              position: 'absolute',
              zIndex: 10000,
              backgroundColor: C('Background/Light/System'),
            }}
          >
            <View
              style={{
                backgroundColor: C('Picture/Light/Overlay'),
                justifyContent: 'center',
                alignContent: 'center',
                alignItems: 'center',
                width: '100%',
                height: '100%',
              }}
            >
              <Svg icon={'pdf'} width={scale(23)} height={scale(29)} color="Label/Light/Quaternary" />
              <Text style={[font.Footnote, { color: C('Label/Light/Quaternary') }]}>{i18n.t('Processing...')}</Text>
            </View>
          </View>
        )}
        {loading ? (
          <SmallLoading />
        ) : possibleAddImage ? (
          <View
            style={{
              width: '100%',
              height: '100%',
              justifyContent: 'center',
              alignItems: 'center',
            }}
          >
            <TouchableOpacity
              activeOpacity={1}
              onPress={pressImage}
              style={{
                width: '100%',
                height: '100%',
                justifyContent: 'center',
                alignItems: 'center',
              }}
            >
              <View
                style={{
                  backgroundColor: noOverlay ? 'transparent' : C('Overlay/Light/Picture white'),
                  width: '100%',
                  height: '100%',
                  position: 'absolute',
                  zIndex: 3,
                  borderRadius: scale(borderRadius),
                }}
              />
              <View
                style={{
                  backgroundColor: noOverlay ? 'transparent' : C('Overlay/Light/Dim light'),
                  width: '100%',
                  height: '100%',
                  position: 'absolute',
                  zIndex: 2,
                  borderRadius: scale(borderRadius),
                }}
              />
              <Svg
                icon="addPicture"
                color={'Label/Light/Tertiary'}
                width={scale(image.width < 200 ? 38 : 72)}
                height={scale(image.width < 200 ? 38 : 72)}
              />
            </TouchableOpacity>
          </View>
        ) : (
          <Image
            {...{
              shadowColor: 'transparent',
              isShadow: Boolean(imageLink),
              isOverlay: !disableImageOverlay,
              borderRadius: scale(borderRadius),
              resizeMode: 'contain',
              style: {
                width: '100%',
                height: '100%',
                resizeMode: 'contain',
              },
              source: uploadedImage ? uploadedImage : { uri: imageLink },
              alt: 'img',
              C,
              data: imageHandlingContext,
              action: onPress,
              scale,
              font,
            }}
          />
        )}
      </View>
    </View>
  );
};

export default ColorWrapper(Context);
